// tslint:disable:jsx-boolean-value
import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import Page from 'models/IObject';
import useRealtimeDocuments from 'hooks/use-realtime-documents';
import hash from 'json-stable-stringify';
import clamp from 'lodash/clamp';
import uniqBy from 'lodash/uniqBy';
import TranslatedText from 'components/i18n/TranslatedText';
import InfiniteScroll from 'components/ui/InfiniteScroll';
import PageMatch, { PageMatchProps } from './PageMatch';
import { CHANNEL_NAV_SCROLL } from 'global-ids';
import { moveToStartByIndex } from 'array-utils';
import { useSelector } from 'react-redux';
import { getCurrentPageId } from 'services/app/selectors';
import { ADMIN_THIN_SCROLL_BAR, HIDE_SCROLLBAR } from 'style/mixins';
import { ADMIN_TEXT_300 } from 'style/constants';
import { ADMIN_TEXT_BODY_XS_MEDIUM } from 'style/design-system/textStyles';
import IObject from 'models/IObject';
import { getActiveSiteFeatures } from 'services/app/selectors/common';
import { Feature } from 'services/feature-gate';

export interface PageResultsProps {
  currentPageId?: string;
  draftDocs: Record<string, Page>;
  homeId: string;
  onLoadMore: () => void;
  pages: Page[];
}

export default function({
  draftDocs,
  homeId,
  onLoadMore,
  pages,
}: PageResultsProps) {
  const seenIds = useRef<Record<string, boolean>>({});
  const currentPageId = useSelector(getCurrentPageId);
  const features = useSelector(getActiveSiteFeatures);

  const [docSearchList, draftPages] = useMemo(() => {
    const searchList: { collection: string, id: string }[] = [];
    const pagesList: {
      draftPage: IObject,
      stalePage: IObject,
    }[] = [];

    for (const stalePage of pages) {
      searchList.push({
        collection: features[Feature.PAGES_V3] ? 'pages' : 'objects',
        id: stalePage._id,
      });

      const id = stalePage._id;
      seenIds.current[id] = true;
      const draftPage = draftDocs[id];

      pagesList.push({
        draftPage,
        stalePage,
      });
    }
    return [searchList, pagesList];
  }, [hash(pages)]);

  const [realtimePages] = useRealtimeDocuments<Page>(docSearchList);

  const existingPages = useMemo(() => (
    realtimePages.map((realtimePage, i) => {
      const { draftPage, stalePage } = draftPages[i];
      const page = (draftPage && {
        ...draftPage,
        slug: (realtimePage || stalePage || draftPage).slug,
      }) || realtimePage || stalePage;
      return {
        hasChanges: !!draftDocs[page._id],
        image: page.data?.channelSelectActiveImageUrl || null,
        isCurrentPage: currentPageId === page._id,
        isHome: page._id === homeId,
        name: page.seo?.title || page.data?.name || page.slug,
        pageId: page._id,
        slug: page?.slug,
        type: page.type,
      } as PageMatchProps;
    })
  ), [hash(draftPages), hash(realtimePages), homeId, currentPageId]);

  const newPages = Object.entries(draftDocs)
    .filter(([id]) => !seenIds.current[id])
    .map(([_, page]) => (
      {
        hasChanges: !!draftDocs[page._id],
        image: page.data?.channelSelectActiveImageUrl || null,
        isCurrentPage: currentPageId === page._id,
        isHome: page._id === homeId,
        name: page.seo?.title || page.data?.name || page.slug,
        pageId: page._id,
        slug: page.slug,
        type: page.type,
      } as PageMatchProps
    ));

  const sortDocs = (results: PageMatchProps[]) => {
    const filtered = uniqBy(results, 'pageId');
    const homeIndex = filtered.findIndex(result => result.isHome);
    moveToStartByIndex(filtered, homeIndex);
    const currentPageIndex = filtered.findIndex(result => result.isCurrentPage);
    moveToStartByIndex(filtered, currentPageIndex);
    return filtered;
  };

  const channels = existingPages.length ? [...newPages, ...existingPages] : [];
  const hasFilteredResults = channels.length;
  const contentHeight = channels.length * 52;

  return hasFilteredResults ? (
    <PageResults
      height={clamp(contentHeight, 52, 312)}
      id={CHANNEL_NAV_SCROLL}
    >
      <InfiniteScroll
        admin
        disableScroll
        list={sortDocs(channels)}
        offset={23}
        onLoadMore={onLoadMore}
        pageSize={10}
      >
        {(itemProps) => <PageMatch {...itemProps} />}
      </InfiniteScroll>
    </PageResults>
  ) : (
    <TranslatedText
      component={NoResults}
      stringKey="ADMIN_LABEL_NO_MATCHES_FOUND"
    />
  );
}

const PageResults = styled.div<{height: number}>`
  &:hover {
    ${ADMIN_THIN_SCROLL_BAR}
  }
  &:not(:hover) {
    ${HIDE_SCROLLBAR}
  }
  min-height: ${({ height }) => height}px;
  overflow: hidden auto;
  flex: 1 0 0;
  & > * {
    gap: 6px;
  }
`;

export const NoResults = styled.div.attrs({
  'data-testid': 'channelResultsNoMatchesFound',
})`
  width: 100%;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 15px 12px;
  ${ADMIN_TEXT_BODY_XS_MEDIUM}
  color: ${ADMIN_TEXT_300};
  text-align: center;
`;
